import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input } from '@artosera/ui';
import { ApiError, modeDemo } from '../../lib/api.js';
import { ventesAddonsApi } from '../../lib/addon-ventes.js';
import { usePeut } from '../../auth/usePeut.js';

export function SouscriptionAddon({
  galerieId,
  souscription: s,
}: {
  galerieId: string;
  souscription: {
    id: string;
    etat: string;
    finPeriode: string;
    revision: number;
    raisonSuspension?: string | null;
  };
}) {
  const { t, i18n } = useTranslation('console');
  const peut = usePeut();
  const client = useQueryClient();
  const [maintenant] = useState(() => Date.now());
  const [geste, setGeste] = useState<
    'resilier' | 'annuler-resiliation' | 'suspendre' | 'reactiver' | null
  >(null);
  const [motif, setMotif] = useState('');
  const mutation = useMutation({
    retry: false,
    mutationFn: async () => {
      const fraiches = await ventesAddonsApi.souscriptions(galerieId);
      const actuelle = fraiches.items.find((item) => item.id === s.id);
      if (!actuelle || actuelle.revision !== s.revision)
        throw new ApiError(409, 'conflit.version', t('addons.souscriptions.erreur'), {
          raison: 'revision_souscription_perimee',
        });
      if (geste === 'suspendre' || geste === 'reactiver')
        return ventesAddonsApi.suspension(s.id, geste, {
          revision: s.revision,
          motif: motif.trim(),
        });
      if (!geste) throw new Error('Geste absent');
      return ventesAddonsApi.resiliation(galerieId, s.id, geste, {
        revision: s.revision,
        motif: motif.trim(),
      });
    },
    onSuccess: () => setGeste(null),
    onSettled: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['console', 'addon-ventes'] }),
        client.invalidateQueries({ queryKey: ['console', 'galerie', galerieId] }),
      ]);
    },
  });
  const date = new Date(s.finPeriode).toLocaleDateString(i18n.language, {
    timeZone: 'Europe/Paris',
  });
  return (
    <div>
      <p>{t(`addons.souscriptions.${s.etat}`, { date })}</p>
      {s.raisonSuspension ? <p>{t(`addons.souscriptions.raisons.${s.raisonSuspension}`)}</p> : null}
      {peut('console:manage-addon-sales') &&
      !modeDemo() &&
      ['active', 'resiliee'].includes(s.etat) &&
      +new Date(s.finPeriode) > maintenant ? (
        <Button
          disabled={mutation.isPending}
          onClick={() => {
            setMotif('');
            mutation.reset();
            setGeste(s.etat === 'active' ? 'resilier' : 'annuler-resiliation');
          }}
        >
          {t(
            s.etat === 'active'
              ? 'addons.souscriptions.resilier'
              : 'addons.souscriptions.annulerResiliation',
          )}
        </Button>
      ) : null}
      {peut('console:manage-addon-billing') &&
      !modeDemo() &&
      ['active', 'resiliee', 'suspendue'].includes(s.etat) ? (
        <Button
          disabled={mutation.isPending}
          onClick={() => {
            setMotif('');
            mutation.reset();
            setGeste(s.etat === 'suspendue' ? 'reactiver' : 'suspendre');
          }}
        >
          {t(
            s.etat === 'suspendue'
              ? 'addons.souscriptions.reactiver'
              : 'addons.souscriptions.suspendre',
          )}
        </Button>
      ) : null}
      <Dialog
        open={geste !== null}
        onOpenChange={(open) => {
          if (!mutation.isPending && !open) setGeste(null);
        }}
        title={t('addons.souscriptions.titre')}
        closeLabel={t('addons.annuler')}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!mutation.isPending) mutation.mutate();
          }}
        >
          <p>{t(`addons.souscriptions.confirmation_${geste}`, { date })}</p>
          <label>
            {t('addons.motif')}
            <Input
              required
              maxLength={500}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />
          </label>
          {mutation.isError ? <p role="alert">{t('addons.souscriptions.erreur')}</p> : null}
          <Button type="submit" disabled={mutation.isPending || !motif.trim()}>
            {t('addons.souscriptions.confirmer')}
          </Button>
        </form>
      </Dialog>
    </div>
  );
}
